/**
 * @module config/featureFlags
 * @description Lightweight feature flags to decouple *deploying* code from
 * *releasing* it. A flag defaulting off lets you ship a feature dark; flipping it
 * on makes it visible without another build.
 *
 * Resolution order (later overrides earlier):
 *   1. DEFAULTS below
 *   2. build-time env: VITE_FEATURE_<NAME>=true|false
 *   3. runtime global: window.__FELICE_FLAGS__ = { name: true } (set by an
 *      optional, non-bundled /runtime-config.js you can edit on the server)
 *   4. localStorage "featureFlags" (per-browser manual/dev override)
 *
 * NOTE: the *per-user* go-live lever for an app feature is still the `tasks` app
 * permission (granting it needs no redeploy). This flag is the global on/off
 * (e.g. hide a half-baked feature from everyone, including appadmins).
 */

const DEFAULTS = {
  // Tasks released 2026-06-20 (was dark). Visible where the user is appadmin or
  // has the "tasks" app. Set this false again (or VITE_FEATURE_* / window flag /
  // localStorage) to hide it everywhere.
  tasks: true,
};

function envOverrides() {
  const out = {};
  for (const [key, val] of Object.entries(import.meta.env || {})) {
    const m = /^VITE_FEATURE_(.+)$/.exec(key);
    if (m) out[m[1].toLowerCase()] = String(val).toLowerCase() === "true";
  }
  return out;
}

function localStorageFlags() {
  try {
    return JSON.parse(localStorage.getItem("featureFlags") || "{}");
  } catch {
    return {};
  }
}

const windowFlags = (typeof window !== "undefined" && window.__FELICE_FLAGS__) || {};

export const featureFlags = {
  ...DEFAULTS,
  ...envOverrides(),
  ...windowFlags,
  ...localStorageFlags(),
};

/** True only when the named feature is explicitly enabled. */
export function isFeatureEnabled(name) {
  return featureFlags[name] === true;
}
